'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { createClient, isSupabaseAvailable } from '@/lib/supabase'
import type { UserRole, PermissionOverride } from '@/lib/permissions'
import type { User as AuthUser, AuthChangeEvent, Session } from '@supabase/supabase-js'

/**
 * User data structure from the backend
 */
export interface UserData {
  id: string
  /** Supabase Auth login email. May be a synthetic placeholder — do not display. */
  email: string
  /** Real contact email, or null if the account has none. Use for display/notify. */
  contact_email?: string | null
  username?: string | null
  name?: string
  role: UserRole
  organization_id?: string
  is_active?: boolean
  avatar_url?: string
  /**
   * Per-user permission overrides loaded from user_permissions.
   * Empty array if none. Undefined means not yet loaded.
   */
  permissionOverrides?: PermissionOverride[]
}

/**
 * Return type for useUser hook
 */
export interface UseUserReturn {
  user: UserData | null
  authUser: AuthUser | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  signOut: () => Promise<void>
}

/**
 * Hook to get the current authenticated user
 *
 * Uses onAuthStateChange() listener instead of getSession() to avoid hangs.
 * The middleware handles token refresh, so we just listen for auth events.
 *
 * When Supabase is not configured (local dev):
 * - Returns a mock user for testing
 * - Role can be set via NEXT_PUBLIC_DEV_USER_ROLE env var
 */
export interface UseUserOptions {
  /**
   * Server-resolved user to seed state with. When provided, the hook is
   * never in a "loading with unknown user" state on first render, which is
   * critical because the browser Supabase client cannot read httpOnly
   * session cookies to populate `INITIAL_SESSION`.
   */
  initialUser?: UserData | null
}

export function useUser(options: UseUserOptions = {}): UseUserReturn {
  const { initialUser = null } = options
  const [user, setUser] = useState<UserData | null>(initialUser)
  const [authUser, setAuthUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(!initialUser)
  const [error, setError] = useState<Error | null>(null)

  // Memoize the supabase client to prevent infinite re-renders
  const supabase = useMemo(() => createClient(), [])

  const redirectInactiveUser = useCallback(() => {
    if (typeof window === 'undefined') return
    if (window.location.pathname === '/login') return
    window.location.assign('/login?error=account_inactive')
  }, [])

  /**
   * Fetch user profile from the users table
   */
  const fetchUserProfile = useCallback(async (
    authUserId: string,
    email: string,
    authMetadata?: Record<string, unknown>
  ): Promise<UserData | null> => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('id, email, contact_email, username, name, role, organization_id, is_active, avatar_url')
        .eq('id', authUserId)
        .limit(1)
        .maybeSingle()

      if (profileError || !profile) {
        // User authenticated but no profile or RLS blocking
        // Try to get role from auth metadata (set during signup)
        console.warn('[admin-check][useUser] No profile row found', {
          code: profileError?.code,
          message: profileError?.message,
          details: profileError?.details,
        })
        const metadataRole = authMetadata?.role as UserRole | undefined
        const metadataOrgId = authMetadata?.organization_id as string | undefined

        if (!metadataRole) {
          console.warn('[admin-check][useUser] No role in auth metadata either', authMetadata)
          return null
        }

        return {
          id: authUserId,
          email: email,
          role: metadataRole,
          organization_id: metadataOrgId,
          is_active: true,
        }
      }

      if (profile.is_active === false) {
        return {
          id: profile.id,
          email: profile.email,
          contact_email: profile.contact_email ?? null,
          username: profile.username ?? null,
          name: profile.name || undefined,
          role: profile.role as UserRole,
          organization_id: profile.organization_id || undefined,
          is_active: false,
          avatar_url: profile.avatar_url || undefined,
          permissionOverrides: [],
        }
      }

      // Load per-user permission overrides. Tolerate failure (table may not
      // be migrated yet, RLS may be misconfigured) — fall back to no overrides.
      let permissionOverrides: PermissionOverride[] = []
      try {
        const { data: overrideRows, error: overrideError } = await supabase
          .from('user_permissions')
          .select('resource, action, allowed')
          .eq('user_id', profile.id)
        if (!overrideError && overrideRows) {
          permissionOverrides = overrideRows as PermissionOverride[]
        }
      } catch {
        // ignore
      }

      return {
        id: profile.id,
        email: profile.email,
        contact_email: profile.contact_email ?? null,
        username: profile.username ?? null,
        name: profile.name || undefined,
        role: profile.role as UserRole,
        organization_id: profile.organization_id || undefined,
        is_active: profile.is_active ?? true,
        avatar_url: profile.avatar_url || undefined,
        permissionOverrides,
      }
    } catch (err) {
      console.error('[admin-check][useUser] Error fetching profile:', err)
      return null
    }
  }, [supabase])

  /**
   * Get mock user for local development
   */
  const getMockUser = useCallback((): UserData => {
    const devRole = (process.env.NEXT_PUBLIC_DEV_USER_ROLE as UserRole) || 'admin'
    return {
      id: 'dev-user-123',
      email: 'dev@localhost',
      name: 'Development User',
      role: devRole,
      organization_id: '00000000-0000-0000-0000-000000000001',
      is_active: true,
    }
  }, [])

  /**
   * Handle auth user - fetch profile and update state
   */
  const handleAuthUser = useCallback(async (authUserData: AuthUser | null) => {
    if (!authUserData) {
      setUser(null)
      setAuthUser(null)
      setIsLoading(false)
      return
    }

    setAuthUser(authUserData)

    // Fetch user profile from database
    const profile = await fetchUserProfile(
      authUserData.id,
      authUserData.email || '',
      authUserData.app_metadata
    )

    if (profile?.is_active === false) {
      setUser(profile)
      setError(new Error('User account is inactive'))
      setIsLoading(false)
      redirectInactiveUser()
      return
    }

    setUser(profile)
    setIsLoading(false)
  }, [fetchUserProfile, redirectInactiveUser])

  /**
   * Refetch user data
   */
  const refetch = useCallback(async () => {
    if (!isSupabaseAvailable()) {
      const mockUser = getMockUser()
      setUser(mockUser)
      setAuthUser(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Use getUser() for explicit refetch - it validates with the server
      const { data: { user: authUserData }, error: authError } = await supabase.auth.getUser()

      if (authError) {
        console.error('[useUser] Refetch error:', authError)
        setError(new Error(authError.message))
        setUser(null)
        setAuthUser(null)
        setIsLoading(false)
        return
      }

      await handleAuthUser(authUserData)
    } catch (err) {
      console.error('[useUser] Refetch error:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch user'))
      setIsLoading(false)
    }
  }, [supabase, getMockUser, handleAuthUser])

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    try {
      if (isSupabaseAvailable()) {
        const { error: signOutError } = await supabase.auth.signOut()
        if (signOutError) throw signOutError
      }
      setUser(null)
      setAuthUser(null)
    } catch (err) {
      console.error('[useUser] Sign out error:', err)
      throw err
    }
  }, [supabase])

  // Initialize auth state
  useEffect(() => {
    // Check if Supabase is configured
    if (!isSupabaseAvailable()) {
      // Local development mode - use mock user
      const mockUser = getMockUser()
      setUser(mockUser)
      setAuthUser(null)
      setIsLoading(false)
      return
    }

    let mounted = true
    let initialEventFired = false
    const hasSeed = !!initialUser

    // Set up auth state listener FIRST
    // This is the recommended pattern - don't call getSession()
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        if (!mounted) return

        if (event === 'INITIAL_SESSION') {
          initialEventFired = true
          if (session?.user) {
            await handleAuthUser(session.user)
          } else if (hasSeed) {
            // Browser client cannot read httpOnly session cookies. The server
            // seeded us with a real user — keep it, just mark loading done.
            setIsLoading(false)
          } else {
            await handleAuthUser(null)
          }
        } else if (event === 'SIGNED_IN' && session?.user) {
          initialEventFired = true
          await handleAuthUser(session.user)
        } else if (event === 'SIGNED_OUT') {
          initialEventFired = true
          setUser(null)
          setAuthUser(null)
          setIsLoading(false)
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setAuthUser(session.user)
        } else if (event === 'USER_UPDATED' && session?.user) {
          await handleAuthUser(session.user)
        }
      }
    )

    // FALLBACK: INITIAL_SESSION occasionally never fires in the browser when
    // the GoTrueClient's navigator lock is held by a stale owner (dev HMR,
    // other tab, or a lock-timeout loop). If we haven't heard anything within
    // 2s, try getSession() as a second-chance bootstrap. If THAT also hangs
    // past 5s, force-exit loading so the UI doesn't stay blank forever.
    const fallbackTimer = setTimeout(async () => {
      if (!mounted || initialEventFired) return
      console.warn('[admin-check][useUser] INITIAL_SESSION not fired within 2s — calling getSession() fallback')
      try {
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise<{ data: { session: Session | null }, error: null }>((_, reject) =>
          setTimeout(() => reject(new Error('getSession() timed out after 3s')), 3000)
        )
        const result = await Promise.race([sessionPromise, timeoutPromise])
        if (!mounted) return
        if (result.data?.session?.user && !initialEventFired) {
          await handleAuthUser(result.data.session.user)
        }
      } catch (err) {
        if (!mounted) return
        console.error('[admin-check][useUser] getSession() fallback failed', err)
        // Unblock the UI — not authenticated (or lock held)
        setIsLoading(false)
      }
    }, 2000)

    return () => {
      mounted = false
      clearTimeout(fallbackTimer)
      subscription.unsubscribe()
    }
  }, [supabase, getMockUser, handleAuthUser, initialUser])

  // Adopt a server-provided seed that arrives AFTER mount. `useState(initialUser)`
  // only honors the value present on first render; when the root layout re-runs
  // server-side (e.g. `router.refresh()` right after login) it passes a fresh
  // `initialUser`, but the browser can't read the httpOnly cookie to discover the
  // session on its own. Without this, role-gated UI (navbar Admin link) stays
  // stale until a full page reload remounts the hook. Only fill an empty slot —
  // never clobber an already-loaded profile (which carries permission overrides).
  useEffect(() => {
    if (initialUser) {
      if (initialUser.is_active === false) {
        setUser(initialUser)
        setError(new Error('User account is inactive'))
        setIsLoading(false)
        redirectInactiveUser()
        return
      }
      setUser((prev) => prev ?? initialUser)
      setIsLoading(false)
    }
  }, [initialUser, redirectInactiveUser])

  return {
    user,
    authUser,
    isLoading,
    error,
    refetch,
    signOut,
  }
}

/**
 * Get user role - returns null if user has no role assigned
 */
export function getUserRole(user: UserData | null): UserRole | null {
  return user?.role ?? null
}
